import NumberInput from "../../NumberInput";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatIDR } from "../../../utils/helpers/formatIDR";
import { isAutomaticPaymentMethod, normalizeCurrencyInput } from "../../../pages/PosPage/paymentCalculations";
import PaymentQrisPanel from "../PaymentQrisPanel";

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash", description: "Tunai dari customer" },
  { value: "qris", label: "QRIS", description: "QR dinamis sesuai nominal" },
  { value: "transfer", label: "Transfer", description: "Nominal pelunasan otomatis" },
];

const QUICK_CASH_AMOUNTS = [50000, 100000, 150000];

const PaymentSettlementModal = ({
  idModal = "settlement-payment",
  title = "Proses Pelunasan",
  transaction,
  nameLabel = "Nama",
  nameValue,
  loading = false,
  storeSetting,
  onConfirm,
}) => {
  const closeButtonRef = useRef(null);
  const due = Number(transaction?.due || 0);
  const total = Number(transaction?.total || 0);
  const paid = Number(transaction?.pay || 0);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [validationAttempted, setValidationAttempted] = useState(false);

  useEffect(() => {
    setPaymentAmount(due || "");
    setPaymentMethod(transaction?.payment_method || "cash");
    setNote("");
    setValidationAttempted(false);
  }, [due, transaction?.id, transaction?.payment_method]);

  const amount = Number(paymentAmount || 0);
  const appliedAmount = Math.min(amount, due);
  const remainingDue = Math.max(0, due - appliedAmount);
  const change = Math.max(0, amount - due);
  const isInvalid = !transaction?.id || amount <= 0 || due <= 0 || !paymentMethod;
  const productSummary = useMemo(
    () => (transaction?.details || []).map((item) => item?.product_name).filter(Boolean).join(", "),
    [transaction?.details],
  );

  const handleMethodChange = (method) => {
    setPaymentMethod(method);
    if (isAutomaticPaymentMethod(method)) {
      setPaymentAmount(due);
    }
  };

  const handleSubmit = async () => {
    setValidationAttempted(true);
    if (isInvalid || loading) return;

    const success = await onConfirm?.(transaction.id, {
      payment_amount: amount,
      payment_method: paymentMethod,
      note,
    });

    if (success) {
      closeButtonRef.current?.click();
    }
  };

  return (
    <div
      className="modal modal-blur fade"
      id={`modal-${idModal}`}
      tabIndex="-1"
      role="dialog"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false">
      <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <div className="text-uppercase text-primary small fw-bold mb-1">Pembayaran sisa tagihan</div>
              <h5 className="modal-title">{title}</h5>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Tutup"
            />
          </div>

          <div className="modal-body">
            <div className="row g-4">
              <div className="col-lg-5">
                <div className="card bg-light border-0 h-100">
                  <div className="card-body">
                    <div className="text-secondary small fw-bold text-uppercase mb-3">Ringkasan transaksi</div>
                    <div className="d-flex justify-content-between gap-3 mb-2">
                      <span className="text-secondary">{nameLabel}</span>
                      <span className="fw-bold text-end">{nameValue || "-"}</span>
                    </div>
                    {productSummary ? (
                      <div className="d-flex justify-content-between gap-3 mb-3">
                        <span className="text-secondary">Produk</span>
                        <span className="fw-semibold text-end text-truncate" title={productSummary}>{productSummary}</span>
                      </div>
                    ) : null}
                    <div className="d-flex justify-content-between gap-3 mb-2 pt-3 border-top">
                      <span className="text-secondary">Total</span>
                      <span className="fw-bold">{formatIDR(total)}</span>
                    </div>
                    <div className="d-flex justify-content-between gap-3 mb-3">
                      <span className="text-secondary">Sudah dibayar</span>
                      <span className="fw-bold text-success">{formatIDR(paid)}</span>
                    </div>
                    <div className="rounded-3 bg-danger-lt p-3">
                      <small className="text-secondary d-block">Sisa tagihan</small>
                      <strong className="h2 text-danger mb-0">{formatIDR(due)}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-7">
                <div className="mb-3">
                  <label className="form-label">Metode Pembayaran <span className="akas-required-mark">*</span></label>
                  <div className="form-selectgroup form-selectgroup-boxes d-flex gap-2 draft-payment-methods">
                    {PAYMENT_METHODS.map((method) => (
                      <label className="form-selectgroup-item flex-fill" key={method.value}>
                        <input
                          type="radio"
                          name={`settlement-payment-method-${transaction?.id || "sale"}`}
                          value={method.value}
                          className="form-selectgroup-input"
                          checked={paymentMethod === method.value}
                          onChange={() => handleMethodChange(method.value)}
                        />
                        <span className="form-selectgroup-label d-flex flex-column align-items-start h-100 p-3">
                          <strong>{method.label}</strong>
                          <small className="text-secondary mt-1">{method.description}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Nominal Pelunasan <span className="akas-required-mark">*</span></label>
                  <div className="input-group">
                    <span className="input-group-text bg-primary text-white">Rp</span>
                    <NumberInput
                      type="number"
                      min="0"
                      className={`form-control form-control-lg ${validationAttempted && amount <= 0 ? "is-invalid" : ""}`}
                      value={paymentAmount}
                      readOnly={isAutomaticPaymentMethod(paymentMethod)}
                      onWheel={(event) => event.currentTarget.blur()}
                      onChange={(event) => setPaymentAmount(normalizeCurrencyInput(event.target.value))}
                      placeholder="0"
                    />
                  </div>
                  {validationAttempted && amount <= 0 ? (
                    <div className="invalid-feedback d-block">Nominal pelunasan minimal Rp1.</div>
                  ) : null}
                  {isAutomaticPaymentMethod(paymentMethod) ? (
                    <div className="form-hint mt-1">Nominal otomatis mengikuti sisa tagihan.</div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setPaymentAmount(due)}>
                        Uang Pas
                      </button>
                      {QUICK_CASH_AMOUNTS.map((quickAmount) => (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          key={quickAmount}
                          onClick={() => setPaymentAmount(quickAmount)}>
                          {quickAmount / 1000}rb
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {paymentMethod === "qris" ? (
                  <PaymentQrisPanel amount={appliedAmount} storeSetting={storeSetting} />
                ) : null}

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <div className="rounded-3 border p-3 h-100">
                      <small className="text-secondary d-block">Sisa setelah bayar</small>
                      <strong className={remainingDue > 0 ? "text-danger" : "text-success"}>
                        {formatIDR(remainingDue)}
                      </strong>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="rounded-3 border p-3 h-100">
                      <small className="text-secondary d-block">Kembalian</small>
                      <strong>{formatIDR(change)}</strong>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="form-label">Catatan</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    maxLength="500"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Opsional, contoh: pelunasan via rekening BCA"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <div className="me-auto">
              <span className={`badge ${remainingDue === 0 ? "bg-success-lt" : "bg-warning-lt"}`}>
                Setelah dibayar: {remainingDue === 0 ? "Lunas" : `Sisa ${formatIDR(remainingDue)}`}
              </span>
            </div>
            <button type="button" className="btn btn-link link-secondary" data-bs-dismiss="modal" disabled={loading}>
              Batal
            </button>
            <button type="button" className="btn btn-primary" disabled={loading} onClick={handleSubmit}>
              {loading ? "Memproses..." : "Simpan Pelunasan"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSettlementModal;
